import { z } from 'zod'
import { calendarDaySchema, wallClockSchema } from '../validators/temporal.validator.js'

/**
 * One timed segment of an event as a calendar reads it: what it is called, which
 * room it is in, when it runs, and whether its plan is settled.
 *
 * The floor plan itself is deliberately absent. A month of events carries hundreds
 * of phases, and the objects on them are the heaviest thing in the schema — the
 * calendar needs to place a block and say what is in it, not draw the furniture.
 */
export const calendarPhaseDtoSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  spaceName: z.string(),
  startsAt: wallClockSchema.nullable(),
  endsAt: wallClockSchema.nullable(),
  /** Confirmed: frozen to the layout and inventory it was planned against. */
  locked: z.boolean(),
})

/**
 * An event on the calendar. `date` is the day it belongs to (an event without one
 * is a draft and never appears); `startsAt`/`endsAt` are the window when someone
 * has set times, which is what a week or day view places it by.
 *
 * `unconfirmedPhaseCount` is the number the attention marker is drawn from, and it
 * is already zero for a settled event — the API decides that, so two surfaces
 * cannot disagree about whether a plan still needs confirming.
 */
export const calendarEventDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** The day it belongs to — a calendar day, like `EventDto.date`. */
  date: calendarDaySchema,
  /** Wall-clock times at the venue, never converted. */
  startsAt: wallClockSchema.nullable(),
  endsAt: wallClockSchema.nullable(),
  /**
   * The tracks it occupies. Empty means venue-wide.
   *
   * An event may run across several — the calendar draws it across their lanes
   * rather than picking one. See `docs/event-scheduling.md`.
   */
  trackIds: z.array(z.string().uuid()),
  guestCount: z.number().int().nonnegative(),
  phaseCount: z.number().int().nonnegative(),
  unconfirmedPhaseCount: z.number().int().nonnegative(),
  clientNames: z.array(z.string()),
  phases: z.array(calendarPhaseDtoSchema),
})

export type CalendarEventDto = z.infer<typeof calendarEventDtoSchema>

/**
 * A date held for an inquiry, on the calendar. Kept apart from `events` so
 * nothing that counts bookings mistakes a hold for one: it is spoken for, not
 * taken. `trackIds` are the inquiry's; empty means it isn't on a track.
 */
export const calendarHoldDtoSchema = z.object({
  eventId: z.string().uuid(),
  eventName: z.string(),
  date: calendarDaySchema,
  endsOn: calendarDaySchema,
  trackIds: z.array(z.string().uuid()),
  clientNames: z.array(z.string()),
})

export type CalendarHoldDto = z.infer<typeof calendarHoldDtoSchema>

/** A venue's track, named so the calendar can label a lane and color a chip. */
export const calendarTrackDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  sortOrder: z.number().int(),
})

export type CalendarTrackDto = z.infer<typeof calendarTrackDtoSchema>

/**
 * What one venue has on between two dates.
 *
 * Returned as a whole rather than as a list of events, because the calendar needs
 * the venue's tracks to color by even in a month with nothing in it — and asking
 * for those separately would make an empty month two requests and a race.
 */
export const venueCalendarDtoSchema = z.object({
  venueId: z.string().uuid(),
  venueName: z.string(),
  /** The window actually returned, echoed back so a client can trust its cache. */
  /** The window asked for, echoed back — inclusive calendar days. */
  from: calendarDaySchema,
  to: calendarDaySchema,
  concurrentEvents: z.boolean(),
  tracks: z.array(calendarTrackDtoSchema),
  events: z.array(calendarEventDtoSchema),
  /** Active holds on the window's days. Defaulted while an older API is still answering. */
  holds: z.array(calendarHoldDtoSchema).default([]),
})

export type VenueCalendarDto = z.infer<typeof venueCalendarDtoSchema>
